import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SgToastService } from '@sg/ui-kit/core';
import { EMPTY, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { SgLogglyTracker } from './logging/loggly.service';
import { UserSessionService } from './user-session.service';

const HTTP_UNAUTHORIZED = 401;

@Injectable()
export class SgHttp401Interceptor implements HttpInterceptor {
  constructor(
    private userSession: UserSessionService,
    private toastService: SgToastService,
    private logger: SgLogglyTracker
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler) {
    return next.handle(req).pipe(
      catchError((error) => {
        if (!(error instanceof HttpErrorResponse) || error.status !== HTTP_UNAUTHORIZED) {
          return throwError(() => error);
        }

        return from(this.userSession.renewToken()).pipe(
          switchMap((renewEvent) => {
            const token = this.userSession.authAdapter.token;
            if (renewEvent?.event !== 'renew' || !token) {
              const detail = renewEvent?.event === 'renewError' ? (renewEvent.error?.message ?? 'none') : 'none';
              return throwError(
                () =>
                  new Error(`Token renewal failed (event=${renewEvent?.event}, hasToken=${!!token}, detail=${detail})`)
              );
            }
            return next.handle(req.clone({ setHeaders: { Authorization: 'Bearer ' + token } }));
          }),
          catchError((renewalOrRetryError) => {
            const isAuthFailure =
              renewalOrRetryError instanceof Error && renewalOrRetryError.message?.startsWith('Token renewal failed');
            const isRetry401 =
              renewalOrRetryError instanceof HttpErrorResponse && renewalOrRetryError.status === HTTP_UNAUTHORIZED;

            if (!isAuthFailure && !isRetry401) {
              // Real backend error on the retried request — let it propagate so
              // callers can handle it and the logging interceptor can log it.
              return throwError(() => renewalOrRetryError);
            }

            // Unrecoverable auth failure. We return EMPTY below (so the caller's
            // error branch doesn't also fire a doomed retry), which means the
            // outer logging interceptor never sees this. Log it here explicitly.
            this.logger.error(
              renewalOrRetryError instanceof Error
                ? renewalOrRetryError
                : new Error(`Auth failure: ${String(renewalOrRetryError)}`)
            );

            // Log out immediately. The toast is a non-blocking notification and
            // must not gate the session teardown — otherwise the user can keep
            // clicking and firing doomed requests during the ~10s the toast is up.
            this.userSession.logout();
            this.toastService.error({
              title: 'Authorization Error',
              description: 'You are not authorized to perform this operation',
            });

            return EMPTY;
          })
        );
      })
    );
  }
}
